import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Paper,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BranchList: React.FC = () => {
  const { t } = useTranslation('other');
  const [error, setError] = useState<string | null>(null);
  const { currentUser, branches } = useAuth(); // ✅ AuthContext'ten branches alınıyor
  const navigate = useNavigate();

  // Gerçek şubeler ("Şubeye Ait Değil" hariç)
  const realBranches = branches.filter(b => b.id !== "null");
  const loading = !currentUser;

  useEffect(() => {
    if (!currentUser?.uid) {
      setError(t('reservations.branchList.errors.userNotLoggedIn'));
    } else {
      setError(null);
    }
  }, [currentUser, t]);

  const handleBranchClick = (branchId: string) => {
    navigate(`/reservations/branch/${branchId}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" color="primary" sx={{ fontWeight: 600 }}>
          {t('reservations.branchList.title')}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      
      {/* Hata bildirimi */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.branchList.table.branchName')}</TableCell>
                  <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.branchList.table.address')}</TableCell>
                  <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.branchList.table.phone')}</TableCell>
                  <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.branchList.table.email')}</TableCell>
                  <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.branchList.table.type')}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                    {t('reservations.branchList.table.action')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {realBranches.map((branch: any) => (
                  <TableRow key={branch.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{branch.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {branch.address || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {branch.phone || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {branch.email || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={branch.isHeadquarter ? t('reservations.branchList.type.headquarter') : t('reservations.branchList.type.branch')}
                        color={branch.isHeadquarter ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleBranchClick(branch.id)}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {t('reservations.branchList.actions.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {realBranches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('reservations.branchList.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
};

export default BranchList; 